import { FileText } from 'lucide-react';
import { useTheme } from '../../../hooks/useTheme';
import type { CustomerOrderTemplateOption } from '../../../lib/hooks/useCustomerOrderTemplates';

/**
 * Scelta del template documento per un ordine (PDF o invio al magazzino).
 * Il predefinito delle impostazioni ordini è evidenziato e in cima.
 */
export function CustomerOrderTemplatePicker({
  isOpen,
  title,
  templates,
  defaultTemplateId,
  onSelect,
  onClose,
  extraOption,
  description,
}: {
  isOpen: boolean;
  title: string;
  templates: CustomerOrderTemplateOption[];
  defaultTemplateId?: string | null;
  onSelect: (template: CustomerOrderTemplateOption) => void;
  onClose: () => void;
  /** Voce aggiuntiva in fondo, es. la stampa standard quando il flusso ordini è spento */
  extraOption?: { label: string; onSelect: () => void };
  /** Testo sotto il titolo (default: «Scegli il template del documento.») */
  description?: string;
}) {
  const { isDark } = useTheme();
  if (!isOpen) return null;
  const sorted = [...templates].sort((a, b) => Number(b.id === defaultTemplateId) - Number(a.id === defaultTemplateId));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={title}
        className={`relative rounded-xl shadow-xl p-6 max-w-sm w-full ${isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}>
        <h3 className={`text-base font-semibold mb-1 ${isDark ? 'text-gray-100' : 'text-[#37352F]'}`}>{title}</h3>
        <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-[#787774]'}`}>{description || 'Scegli il template del documento.'}</p>
        <div className="flex flex-col gap-1.5">
          {sorted.map(t => {
            const isDefault = t.id === defaultTemplateId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect(t)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                  isDefault
                    ? isDark ? 'border-[#2383E2] bg-blue-500/10 text-gray-200' : 'border-[#2383E2] bg-blue-50 text-[#37352F]'
                    : isDark ? 'border-gray-700 text-gray-200 hover:bg-gray-800' : 'border-[rgba(55,53,47,0.16)] text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
                }`}
              >
                <FileText className={`w-4 h-4 shrink-0 ${isDark ? 'text-gray-500' : 'text-[#787774]'}`} />
                <span className="font-medium truncate flex-1">{t.name}</span>
                {isDefault && (
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-[#2383E2]'}`}>Predefinito</span>
                )}
              </button>
            );
          })}
          {extraOption && (
            <button
              type="button"
              onClick={extraOption.onSelect}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-[rgba(55,53,47,0.16)] text-[#787774] hover:bg-[rgba(55,53,47,0.04)]'
              }`}
            >
              <FileText className="w-4 h-4 shrink-0" />
              <span className="truncate flex-1">{extraOption.label}</span>
            </button>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <button type="button" onClick={onClose}
            className={`px-3 py-1.5 rounded text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-[#787774] hover:bg-[rgba(55,53,47,0.08)]'}`}>
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}
